import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';

import { BasicService } from '../../services/basic.service';
import { TimezoneService } from '../../services/timezone.service';

interface Diff {
  text: string;
  interval: number;
}

/**
 * This component displays the relative date or time from the input date to now (eg : 2 minutes, 11 days...).
 * And refresh it in realtime if the difference is in sec, min or hour (not refreshed for days, months and years).
 * It take in account the timezone of the user.
 */
@Component({
  selector: 'app-relative-date-time',
  templateUrl: './relative-date-time.component.html',
})
export class RelativeDateTimeComponent implements OnInit, OnDestroy {
  @Input() date: string;

  relativeDate: string;
  intervalId: any;

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private timezoneService: TimezoneService
  ) {}

  ngOnInit(): void {
    const diff = this.getDiff();

    // init date with or without interval
    this.relativeDate = diff.text;

    if (diff.interval) {
      this.configureInterval(diff);
    }
  }

  configureInterval(diff: Diff) {
    this.intervalId = setInterval(
      diffParam => {
        const newDiff = this.getDiff();

        this.relativeDate = newDiff.text;

        if (!newDiff.interval) {
          clearInterval(this.intervalId);
        } else if (newDiff.interval !== diffParam.interval) {
          clearInterval(this.intervalId);
          this.configureInterval(newDiff);
        }
      },
      diff.interval,
      diff
    );
  }

  /**
   * @param dateToCompare used for tests, by default it's the current date
   * @return object containing the string as a string, + the interval for when to refresh
   */
  getDiff(dateToCompare?: string): Diff {
    const date = this.timezoneService.getMomentWithTimeZone(this.alcmeonContextService.self.timezone, this.date);
    const now = this.timezoneService.getMomentWithTimeZone(this.alcmeonContextService.self.timezone, dateToCompare);

    const getText = (value: number, key: string) => `${value} ${this.basicService.translate(key)}`;

    // No interval for years months and days, because the page will be refreshed before.

    const yearsDiff = Math.abs(date.diff(now, 'y'));
    if (yearsDiff) {
      const unitKey = yearsDiff === 1 ? 'year' : 'years';

      return {
        text: getText(yearsDiff, unitKey),
        interval: null,
      };
    }

    const monthsDiff = Math.abs(date.diff(now, 'M'));
    if (monthsDiff) {
      const unitKey = monthsDiff === 1 ? 'month' : 'months';

      return {
        text: getText(monthsDiff, unitKey),
        interval: null,
      };
    }

    const daysDiff = Math.abs(date.diff(now, 'd'));
    if (daysDiff) {
      const unitKey = daysDiff === 1 ? 'day' : 'days';

      return {
        text: getText(daysDiff, unitKey),
        interval: null,
      };
    }

    // For hours, we set an inverval corresponding to the minutes remaining to go to the next hour.
    // The goal is to refresh in 2 minutes if we have 1h58m, and not in 1h.

    const hoursDiff = Math.abs(date.diff(now, 'h'));
    const minutesDiff = Math.abs(date.diff(now, 'm'));

    if (hoursDiff) {
      const unitKey = hoursDiff === 1 ? 'hour' : 'hours';

      let interval = 1000 * 60 * 60;

      const modulo = minutesDiff % 60;
      if (minutesDiff && modulo) {
        interval = 1000 * 60 * (60 - modulo);
      }

      return {
        text: getText(hoursDiff, unitKey),
        interval,
      };
    }

    // For minutes, we set an inverval corresponding to the secondes remaining to go to the next minute.
    // The goal is to refresh in 5 secondes if we have 1m55s, and not in 1 min.

    const secondsDiff = Math.abs(date.diff(now, 's'));

    if (minutesDiff) {
      const unitKey = minutesDiff === 1 ? 'minute' : 'minutes';

      let interval = 1000 * 60;

      const modulo = secondsDiff % 60;
      if (secondsDiff && modulo) {
        interval = 1000 * (60 - modulo);
      }

      return {
        text: getText(minutesDiff, unitKey),
        interval,
      };
    }

    // For seconds, we do not want to update every second, so we display "< 1 minute",
    // We set an inverval corresponding to the secondes remaining to go to the next minute.
    if (secondsDiff) {
      return {
        text: `< ${getText(1, 'minute')}`,
        interval: 1000 * (60 - secondsDiff),
      };
    }

    return { text: getText(0, 'second'), interval: 1000 };
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalId);
  }
}
